import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import { useQuery } from "@tanstack/react-query";
import API from "util/API";

import RestaurantHeader from "./RestaurantHeader";
import useRestaurantInfo from "./useRestaurantInfo";
import { queueRefLabel, queueStatusChange } from "./queueFormUtils";
import {
  notificationPermission,
  registerQueueServiceWorker,
  requestNotificationPermission,
  showQueueNotification,
} from "./queueNotifications";

const POLL_MS = 15000;
const FINISHED = ["seated", "cancelled"];

const StatusBody = ({ status }) => {
  if (status.status === "called") {
    return (
      <Alert
        severity="success"
        icon={<NotificationsActiveIcon fontSize="inherit" />}
        sx={{ fontSize: "1.1rem", alignItems: "center" }}
      >
        <strong>Your table is ready!</strong> Please come to the front counter now.
      </Alert>
    );
  }
  if (status.status === "seated") {
    return (
      <Stack alignItems="center" spacing={1}>
        <RestaurantIcon color="success" sx={{ fontSize: 48 }} />
        <Typography variant="h6">You&rsquo;ve been seated. Enjoy your meal!</Typography>
      </Stack>
    );
  }
  if (status.status === "cancelled") {
    return (
      <Alert severity="warning">
        You&rsquo;re no longer in the queue. Please speak to our staff if this is a mistake.
      </Alert>
    );
  }
  return (
    <Stack alignItems="center" spacing={1}>
      <Typography variant="h6">
        {status.position === 1
          ? "You're next in line"
          : status.position
            ? `You're number ${status.position} in line`
            : "You're in the queue"}
      </Typography>
      {status.waitingMinutes ? (
        <Typography variant="body1">
          Estimated wait: about <strong>{status.waitingMinutes} minutes</strong>
        </Typography>
      ) : null}
      <Alert severity="info" sx={{ textAlign: "left", width: "100%" }}>
        Please stay nearby and show this number to our staff. This page updates automatically.
      </Alert>
    </Stack>
  );
};

StatusBody.propTypes = {
  status: PropTypes.shape({
    status: PropTypes.string,
    position: PropTypes.number,
    waitingMinutes: PropTypes.number,
  }).isRequired,
};

const QueueStatus = (props) => {
  const { restaurantId, reference } = props.match.params;
  const { data: restaurant, isLoading: loadingRestaurant } = useRestaurantInfo(restaurantId);
  const [permission, setPermission] = useState(notificationPermission());
  const [copied, setCopied] = useState(false);
  const previous = useRef(null);
  const baseTitle = useRef(document.title);

  const { data: status, error, isLoading } = useQuery({
    queryKey: ["queueStatus", restaurantId, reference],
    queryFn: async () => {
      const response = await API.get("/queueStatus", { params: { restaurantId, reference } });
      return response.data;
    },
    // Stop polling once the guest is seated or removed.
    refetchInterval: (query) =>
      query.state.data && FINISHED.includes(query.state.data.status) ? false : POLL_MS,
    refetchIntervalInBackground: true,
    staleTime: 0,
    retry: (count, err) => !(err && err.response && err.response.status === 404) && count < 3,
  });

  useEffect(() => {
    registerQueueServiceWorker();
  }, []);

  useEffect(() => {
    if (!status) return;
    const change = queueStatusChange(previous.current, status);
    previous.current = status;
    if (!change) return;
    showQueueNotification({
      ...change,
      icon: restaurant && restaurant.logo,
      url: window.location.pathname,
    });
    if (navigator.vibrate) navigator.vibrate([300, 150, 300]);
    document.title = `🔔 ${change.title}`;
  }, [status, restaurant]);

  useEffect(() => {
    const title = baseTitle.current;
    return () => {
      document.title = title;
    };
  }, []);

  const enableNotifications = async () => {
    await registerQueueServiceWorker();
    setPermission(await requestNotificationPermission());
  };

  const copyLink = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: "My queue status", url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
      }
    } catch (err) {
      // Dismissing the share sheet rejects; nothing to report.
      console.info("queue link share cancelled", err);
    }
  };

  const notFound = error && error.response && error.response.status === 404;
  const active = status && !FINISHED.includes(status.status);

  return (
    <Box sx={{ px: 2, py: { xs: 3, sm: 6 }, display: "flex", justifyContent: "center" }}>
      <Card sx={{ width: "100%", maxWidth: 480 }} elevation={2}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {loadingRestaurant ? null : <RestaurantHeader restaurant={restaurant} />}

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : notFound ? (
            <Alert severity="warning">
              We couldn&rsquo;t find this queue entry. Please check the link or speak to our staff.
            </Alert>
          ) : error && !status ? (
            <Alert severity="error">Could not load your queue status. Retrying…</Alert>
          ) : status ? (
            <Stack spacing={3} sx={{ textAlign: "center" }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {status.name ? `${status.name}, your queue number` : "Your queue number"}
                </Typography>
                <Typography variant="h1" component="p" sx={{ fontWeight: 700, fontSize: "4.5rem", lineHeight: 1.1 }}>
                  {queueRefLabel(status)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {status.guestNum} {Number(status.guestNum) === 1 ? "guest" : "guests"}
                </Typography>
              </Box>

              <StatusBody status={status} />

              {active ? (
                <Stack spacing={1}>
                  {permission === "granted" ? (
                    <Typography variant="body2" color="success.main">
                      Notifications are on. Keep this page open and we&rsquo;ll alert you.
                    </Typography>
                  ) : permission === "default" ? (
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<NotificationsActiveIcon />}
                      onClick={enableNotifications}
                    >
                      Notify me when my table is ready
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {permission === "denied"
                        ? "Notifications are blocked in your browser settings. "
                        : ""}
                      Keep this page open and we&rsquo;ll show an alert here when your table is ready.
                    </Typography>
                  )}
                  <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={copyLink}>
                    {copied ? "Link copied" : "Save or share this page link"}
                  </Button>
                </Stack>
              ) : null}
            </Stack>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
};

QueueStatus.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      restaurantId: PropTypes.string,
      reference: PropTypes.string,
    }),
  }).isRequired,
};

export default QueueStatus;
